import type { Dispatch, FC, ReducerAction, ReducerState } from "react";
import {
  useEffect,
  useMemo,
  createContext,
  useContext,
  useReducer,
} from "react";
import type { GlobalReducer, GlobalState } from "~/store/globalState/type";
import type { PasswordValidator } from "@commercekit/remix";
import { useLocation } from "@remix-run/react";

const init: GlobalState = {
  mobileMenu: false,
  isShoppingCartLayout: false,
  curentPage: "",
  showCheckoutInfoBox: false,
};

export const GlobalCtx = createContext<{
  state: ReducerState<GlobalReducer>;
  dispatch: Dispatch<ReducerAction<GlobalReducer>>;
  global: PasswordValidator;
  isShoppingCartLayout: boolean;
  isCheckoutHeaderLayout: boolean;
}>({
  state: init,
  dispatch: () => {},
  isShoppingCartLayout: false,
  isCheckoutHeaderLayout: false,
  global: {
    PASSWORD_LENGTH_MIN: "",
    PASSWORD_REQUIREMENTS: "",
  },
});

const reducer: GlobalReducer = (pre, action) => {
  switch (action.type) {
    case "toggleMobileMenu":
      return { ...pre, mobileMenu: !pre.mobileMenu };
    case "closeMobileMenu":
      return { ...pre, mobileMenu: false };
    case "showShoppingCartLayout":
      return { ...pre, isShoppingCartLayout: true };
    case "showGeneralLayout":
      return { ...pre, isShoppingCartLayout: false };
    case "currentPage":
      return { ...pre, currentPage: action.payload.page };
    case "showCheckoutInfoBox":
      return { ...pre, showCheckoutInfoBox: action.payload.show };
    default:
      return { ...pre };
  }
};

export const GlobalCtxProvider: FC<
  React.PropsWithChildren<{
    global: PasswordValidator;
  }>
> = ({ children, global }) => {
  const currentLocation = useLocation();

  const isOrderConfirmationPage = useMemo(
    () =>
      currentLocation.pathname.split("/")?.includes("thank-you") &&
      !currentLocation.pathname.split("/")?.includes("customer"),
    [currentLocation.pathname]
  );

  const isShoppingCartLayout = useMemo(
    () =>
      isOrderConfirmationPage ||
      currentLocation.pathname.split("/")?.includes("cart") ||
      currentLocation.pathname.split("/")?.includes("checkout"),
    [currentLocation.pathname, isOrderConfirmationPage]
  );

  const isCheckoutHeaderLayout = useMemo(
    () => currentLocation.pathname.split("/")?.includes("checkout"),
    [currentLocation.pathname]
  );

  const [state, dispatch] = useReducer(reducer, {
    ...init,
    isShoppingCartLayout,
  });

  useEffect(() => {
    if (isShoppingCartLayout) {
      dispatch({ type: "showShoppingCartLayout" });
    } else {
      dispatch({ type: "showGeneralLayout" });
    }
  }, [isShoppingCartLayout, dispatch]);
  return (
    <GlobalCtx.Provider
      value={{
        state,
        dispatch,
        global,
        isShoppingCartLayout,
        isCheckoutHeaderLayout,
      }}
    >
      {children}
    </GlobalCtx.Provider>
  );
};

export const useGlobalCtx = () => {
  const {
    state,
    dispatch,
    global,
    isShoppingCartLayout,
    isCheckoutHeaderLayout,
  } =
    useContext(GlobalCtx);
  return {
    state,
    dispatch: {
      toggleMobileMenu: () => dispatch({ type: "toggleMobileMenu" }),
      closeMobileMenu: () => dispatch({ type: "closeMobileMenu" }),
      showShoppingCartLayout: () =>
        dispatch({ type: "showShoppingCartLayout" }),
      showGeneralLayout: () => dispatch({ type: "showGeneralLayout" }),
      setCurrentPage: (page: string) =>
        dispatch({
          type: "currentPage",
          payload: { page },
        }),
      setCheckoutInfoBox: (show: boolean) =>
        dispatch({
          type: "showCheckoutInfoBox",
          payload: { show },
        }),
    },
    global,
    isShoppingCartLayout,
    isCheckoutHeaderLayout,
  };
};

export const useEnv = () => {
  const { global } = useContext(GlobalCtx);
  return global;
};
